import type { ProductRole } from '@academy/domain';
import type { PresentationLibraryWorkspace } from '../routes/presentation-studio-data';
import { hasRouteCapability } from '../routes/route-access';
import { workspaceRouteQueryNamespaces } from '../routes/workspace-route-metadata';
import { createAuthorityQueryKey, defaultQueryStaleTimeMs, seedCachedQuery } from './query-cache';

export const querySnapshotStorageKey = 'academy:query-snapshots';
export const querySnapshotSchemaVersion = 1;
export const querySnapshotTtlMs = 5 * 60_000;
export const maxQuerySnapshotEntryBytes = 48 * 1024;
export const maxQuerySnapshotTotalBytes = 64 * 1024;
export const querySnapshotHydrationMeasure = 'academy:query-snapshot-hydration';

const presentationRoute = '/studio/presentations';
const sha256Pattern = /^[a-f0-9]{64}$/u;

export type PersistedPresentationLibrary = {
  presentations: Array<{
    presentation_id: number;
    title: string;
    status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
    updated_at: string | null;
    latest_version_id: number | null;
    latest_version_no: number | null;
    latest_version_status: 'DRAFT' | 'PUBLISHING' | 'PUBLISHED' | 'RETIRED' | null;
    lesson_count: number;
    can_manage: boolean;
    author_account_id: number;
  }>;
};

export type QuerySnapshotEnvelope = {
  schemaVersion: 1;
  entry: {
    schemaVersion: 1;
    namespace: 'presentation-library';
    accountId: number;
    activeRole: ProductRole;
    route: '/studio/presentations';
    query: 'presentation-library';
    sessionSubjectHash: string;
    sessionGenerationHash: string;
    storedAt: number;
    expiresAt: number;
    data: PersistedPresentationLibrary;
  };
};

export type QuerySnapshotValidator = {
  parseEnvelope: (value: unknown) => QuerySnapshotEnvelope | null;
  projectPresentationLibrary: (value: unknown) => PersistedPresentationLibrary | null;
  restorePresentationLibrary: (
    value: PersistedPresentationLibrary,
  ) => PresentationLibraryWorkspace | null;
};

let installedValidator: QuerySnapshotValidator | null = null;

export const installQuerySnapshotValidator = (validator: QuerySnapshotValidator) => {
  installedValidator = validator;
};

type SnapshotStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type QuerySnapshotAuthority = {
  accountId: number;
  activeRole: ProductRole | null;
  roles: readonly ProductRole[];
  sessionSubjectHash?: string | undefined;
  sessionGenerationHash?: string | undefined;
};

export type QuerySnapshotWriteResult = {
  status: 'written' | 'skipped';
  reason: 'written' | 'unavailable' | 'unauthorized' | 'invalid' | 'oversized';
  bytes: number;
};

export type QuerySnapshotHydrationResult = {
  hydrated: boolean;
  reason:
    | 'hydrated'
    | 'unavailable'
    | 'missing'
    | 'invalid'
    | 'oversized'
    | 'expired'
    | 'authority-mismatch';
  durationMs: number;
};

const storageOrNull = (storage?: SnapshotStorage) => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const byteLength = (value: string) => new TextEncoder().encode(value).byteLength;
const measuredNow = () =>
  typeof performance === 'undefined' || typeof performance.now !== 'function'
    ? Date.now()
    : performance.now();

const startHydrationMeasure = () => {
  if (typeof performance === 'undefined' || typeof performance.mark !== 'function') return;
  try {
    performance.clearMarks(`${querySnapshotHydrationMeasure}:start`);
    performance.clearMeasures(querySnapshotHydrationMeasure);
    performance.mark(`${querySnapshotHydrationMeasure}:start`);
  } catch {
    // Performance entries are optional observability, never part of hydration authority.
  }
};

const finishHydrationMeasure = (startedAt: number) => {
  const durationMs = Math.max(0, Math.round((measuredNow() - startedAt) * 100) / 100);
  if (typeof performance !== 'undefined' && typeof performance.measure === 'function') {
    try {
      performance.measure(querySnapshotHydrationMeasure, `${querySnapshotHydrationMeasure}:start`);
    } catch {
      // Some test environments expose a partial Performance implementation.
    }
  }
  return durationMs;
};

const removeSnapshot = (storage: SnapshotStorage) => {
  try {
    storage.removeItem(querySnapshotStorageKey);
  } catch {
    // Storage can be disabled between reads and writes.
  }
};

const readEnvelope = (storage: SnapshotStorage) => {
  let raw: string | null;
  try {
    raw = storage.getItem(querySnapshotStorageKey);
  } catch {
    return { reason: 'unavailable' as const, envelope: null };
  }
  if (raw === null) return { reason: 'missing' as const, envelope: null };
  if (byteLength(raw) > maxQuerySnapshotTotalBytes) {
    removeSnapshot(storage);
    return { reason: 'oversized' as const, envelope: null };
  }
  try {
    const envelope = installedValidator?.parseEnvelope(JSON.parse(raw)) ?? null;
    if (!envelope || byteLength(JSON.stringify(envelope.entry)) > maxQuerySnapshotEntryBytes) {
      removeSnapshot(storage);
      return { reason: 'invalid' as const, envelope: null };
    }
    return { reason: 'valid' as const, envelope };
  } catch {
    removeSnapshot(storage);
    return { reason: 'invalid' as const, envelope: null };
  }
};

const hasPresentationAuthority = (authority: QuerySnapshotAuthority) =>
  authority.activeRole !== null &&
  authority.roles.includes(authority.activeRole) &&
  hasRouteCapability(authority.roles, 'presentationStudio');

export const persistPresentationLibrarySnapshot = (
  workspace: unknown,
  authority: QuerySnapshotAuthority,
  options: { storage?: SnapshotStorage; now?: number } = {},
): QuerySnapshotWriteResult => {
  const storage = storageOrNull(options.storage);
  if (!storage) return { status: 'skipped', reason: 'unavailable', bytes: 0 };
  if (
    !hasPresentationAuthority(authority) ||
    !authority.activeRole ||
    !sha256Pattern.test(authority.sessionSubjectHash ?? '') ||
    !sha256Pattern.test(authority.sessionGenerationHash ?? '')
  ) {
    removeSnapshot(storage);
    return { status: 'skipped', reason: 'unauthorized', bytes: 0 };
  }

  const data = installedValidator?.projectPresentationLibrary(workspace) ?? null;
  if (!data) {
    removeSnapshot(storage);
    return { status: 'skipped', reason: 'invalid', bytes: 0 };
  }
  const storedAt = options.now ?? Date.now();
  const envelope = installedValidator?.parseEnvelope({
    schemaVersion: querySnapshotSchemaVersion,
    entry: {
      schemaVersion: querySnapshotSchemaVersion,
      namespace: workspaceRouteQueryNamespaces.presentations,
      accountId: authority.accountId,
      activeRole: authority.activeRole,
      route: presentationRoute,
      query: workspaceRouteQueryNamespaces.presentations,
      sessionSubjectHash: authority.sessionSubjectHash,
      sessionGenerationHash: authority.sessionGenerationHash,
      storedAt,
      expiresAt: storedAt + querySnapshotTtlMs,
      data,
    },
  });
  if (!envelope) {
    removeSnapshot(storage);
    return { status: 'skipped', reason: 'invalid', bytes: 0 };
  }
  const entryBytes = byteLength(JSON.stringify(envelope.entry));
  const serialized = JSON.stringify(envelope);
  const totalBytes = byteLength(serialized);
  if (entryBytes > maxQuerySnapshotEntryBytes || totalBytes > maxQuerySnapshotTotalBytes) {
    removeSnapshot(storage);
    return { status: 'skipped', reason: 'oversized', bytes: totalBytes };
  }
  try {
    storage.setItem(querySnapshotStorageKey, serialized);
    return { status: 'written', reason: 'written', bytes: totalBytes };
  } catch {
    return { status: 'skipped', reason: 'unavailable', bytes: totalBytes };
  }
};

export const hydrateAllowlistedQuerySnapshots = (
  authority: QuerySnapshotAuthority,
  options: { storage?: SnapshotStorage; now?: number } = {},
): QuerySnapshotHydrationResult => {
  const startedAt = measuredNow();
  startHydrationMeasure();
  const finish = (result: Omit<QuerySnapshotHydrationResult, 'durationMs'>) => ({
    ...result,
    durationMs: finishHydrationMeasure(startedAt),
  });
  const storage = storageOrNull(options.storage);
  if (!storage) return finish({ hydrated: false, reason: 'unavailable' });
  const read = readEnvelope(storage);
  if (!read.envelope) return finish({ hydrated: false, reason: read.reason });
  const entry = read.envelope.entry;
  const now = options.now ?? Date.now();
  if (entry.expiresAt <= now || entry.storedAt > now) {
    removeSnapshot(storage);
    return finish({ hydrated: false, reason: 'expired' });
  }
  if (
    !hasPresentationAuthority(authority) ||
    entry.accountId !== authority.accountId ||
    entry.activeRole !== authority.activeRole ||
    entry.sessionSubjectHash !== authority.sessionSubjectHash ||
    entry.sessionGenerationHash !== authority.sessionGenerationHash
  ) {
    removeSnapshot(storage);
    return finish({ hydrated: false, reason: 'authority-mismatch' });
  }

  const workspace = installedValidator?.restorePresentationLibrary(entry.data) ?? null;
  if (!workspace) {
    removeSnapshot(storage);
    return finish({ hydrated: false, reason: 'invalid' });
  }
  const key = createAuthorityQueryKey({
    accountId: entry.accountId,
    activeRole: entry.activeRole,
    route: entry.route,
    query: entry.query,
  });
  const seeded = seedCachedQuery(key, workspace, {
    updatedAt: Math.min(entry.storedAt, now - defaultQueryStaleTimeMs),
    source: 'session-hydrated',
  });
  return finish({
    hydrated: seeded,
    reason: seeded ? 'hydrated' : 'invalid',
  });
};

export const purgeAllQuerySnapshots = (storage?: SnapshotStorage) => {
  const target = storageOrNull(storage);
  if (target) removeSnapshot(target);
};

export const purgeQuerySnapshotsForAccount = (accountId: number, storage?: SnapshotStorage) => {
  const target = storageOrNull(storage);
  if (!target) return;
  const read = readEnvelope(target);
  if (!read.envelope || read.envelope.entry.accountId === accountId) removeSnapshot(target);
};
